// Material panel — slide-in side panel of finish options for a `surface`
// hotspot. Two modes:
//
// • Tint mode (Sprint 1, panorama rooms): authored swatches in rooms.json
//   each carry an `[r, g, b]` tint; clicking one applies an emissive tint to
//   the panorama (cheap mood / temperature shift).
// • Variant mode (Sprint 2B, glTF rooms): swatches are derived at runtime
//   from the loaded GLB's KHR_materials_variants extension; clicking one
//   calls applyMaterialVariant() on the renderer for a real PBR swap.
//
// App decides which mode applies at panel-open time. The panel itself only
// knows about generic items (id + label + optional dotColor).

import type { SurfaceHotspot, SurfaceSwatch } from '../app/types';

export type SwatchSelectHandler = (swatch: SurfaceSwatch) => void;

/** Generic option presented in the panel. Used by both tint and variant modes. */
export interface PanelItem {
  id: string;
  label: string;
  /** CSS colour for the swatch dot; defaults to a neutral grey if absent. */
  dotColor?: string;
}

export interface OpenItemsOptions {
  title: string;
  description: string;
  items: PanelItem[];
  /** Item id to render as aria-pressed on open. */
  currentId?: string | null;
  onPick: (item: PanelItem) => void;
}

/**
 * One labeled group of swatches inside the panel. Each section tracks its
 * own selected id and onPick handler, so e.g. a "Tonalizar" section
 * (multiplies diffuse) and a "Acabamento" section (swaps KHR_materials_variants)
 * can coexist without interfering.
 */
export interface PanelSection {
  heading: string;
  items: PanelItem[];
  currentId?: string | null;
  onPick: (item: PanelItem) => void;
  /**
   * Optional dynamic footer text rendered under the section's swatch grid.
   * Receives the currently-pressed item id (null if none) and returns a
   * one-line string to display. Empty string or null hides the footer.
   * Used by Phase 3 surface hotspots to show the cost breakdown for the
   * active finish ("Piso: 24 m² × R$ 765/m² = R$ 18.400").
   */
  footer?: (currentId: string | null) => string | null;
}

export interface OpenSectionsOptions {
  title: string;
  description: string;
  sections: PanelSection[];
}

export class MaterialPanel {
  private readonly root: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly title: HTMLElement;
  private readonly description: HTMLElement;
  private readonly swatchList: HTMLElement;
  private items: PanelItem[] = [];
  private currentItemId: string | null = null;
  private itemHandler: (item: PanelItem) => void = () => {};
  private sections: PanelSection[] | null = null;
  private sectionCurrentIds: (string | null)[] = [];
  private tintHandler: SwatchSelectHandler = () => {};
  private onClosed: (() => void) | null = null;

  constructor() {
    this.root = mustGet('material-panel');
    this.closeBtn = mustGet('material-panel-close') as HTMLButtonElement;
    this.title = mustGet('material-panel-title');
    this.description = mustGet('material-panel-description');
    this.swatchList = mustGet('material-panel-swatches');
    this.closeBtn.addEventListener('click', () => this.close());
  }

  /** Legacy tint-mode subscription — fires when an authored swatch is picked. */
  onSelect(handler: SwatchSelectHandler): void {
    this.tintHandler = handler;
  }

  onClose(handler: () => void): void {
    this.onClosed = handler;
  }

  /** Tint mode: open with the swatches authored on the surface hotspot. */
  open(hotspot: SurfaceHotspot, currentSwatchId: string | null = null): void {
    const items: PanelItem[] = hotspot.swatches.map((s) => ({
      id: s.id,
      label: s.label,
      dotColor: s.dotColor ?? rgbToCss(s.tint),
    }));
    this.openItems({
      title: hotspot.label,
      description: hotspot.description ?? 'Toque para tonalizar',
      items,
      currentId: currentSwatchId ?? items[0]?.id ?? null,
      onPick: (item) => {
        // Reconnect the picked item to its source SurfaceSwatch so the
        // legacy onSelect handler still receives the tint payload.
        const swatch = hotspot.swatches.find((s) => s.id === item.id);
        if (swatch) this.tintHandler(swatch);
      },
    });
  }

  /** Variant mode (or any future generic mode): open with arbitrary items. */
  openItems(opts: OpenItemsOptions): void {
    this.items = opts.items;
    this.currentItemId = opts.currentId ?? opts.items[0]?.id ?? null;
    this.itemHandler = opts.onPick;
    this.sections = null;
    this.title.textContent = opts.title;
    this.description.textContent = opts.description;
    this.renderItems();
    this.root.classList.add('is-open');
  }

  /**
   * Dual-section mode: render two independent swatch grids with their own
   * headings and click handlers. Used when a glTF room has both authored
   * tone swatches AND KHR_materials_variants — clicking a tone tints the
   * active material; clicking a variant swaps the material. Tones survive
   * variant swaps because each section maintains its own selected-id state.
   */
  openSections(opts: OpenSectionsOptions): void {
    this.items = [];
    this.currentItemId = null;
    this.sections = opts.sections;
    this.sectionCurrentIds = opts.sections.map(
      (s) => s.currentId ?? s.items[0]?.id ?? null,
    );
    this.title.textContent = opts.title;
    this.description.textContent = opts.description;
    this.renderSections();
    this.root.classList.add('is-open');
  }

  close(): void {
    if (!this.root.classList.contains('is-open')) return;
    this.root.classList.remove('is-open');
    this.items = [];
    this.currentItemId = null;
    this.sections = null;
    this.sectionCurrentIds = [];
    this.onClosed?.();
  }

  isOpen(): boolean {
    return this.root.classList.contains('is-open');
  }

  // ── Internals ─────────────────────────────────────────────────

  private renderItems(): void {
    this.swatchList.innerHTML = '';
    // Outer container is no longer the grid in section mode — keep the flat
    // case using the legacy single-grid class for visual continuity.
    this.swatchList.classList.add('material-panel__swatches');
    this.swatchList.classList.remove('material-panel__sections');
    for (const item of this.items) {
      this.swatchList.appendChild(this.makeSwatchButton(item, this.currentItemId, (picked) => {
        this.currentItemId = picked.id;
        this.renderItems();
        this.itemHandler(picked);
      }));
    }
  }

  private renderSections(): void {
    if (!this.sections) return;
    this.swatchList.innerHTML = '';
    // Stack of labeled grids instead of a single grid.
    this.swatchList.classList.add('material-panel__sections');
    this.swatchList.classList.remove('material-panel__swatches');
    this.sections.forEach((section, idx) => {
      const wrap = document.createElement('div');
      wrap.className = 'material-panel__section';
      const h = document.createElement('h3');
      h.className = 'material-panel__section-heading';
      h.textContent = section.heading;
      wrap.appendChild(h);
      const grid = document.createElement('div');
      grid.className = 'material-panel__swatches';
      for (const item of section.items) {
        grid.appendChild(
          this.makeSwatchButton(item, this.sectionCurrentIds[idx] ?? null, (picked) => {
            this.sectionCurrentIds[idx] = picked.id;
            this.renderSections();
            section.onPick(picked);
          }),
        );
      }
      wrap.appendChild(grid);
      // Dynamic footer — used by the Material section to show the cost
      // breakdown for the currently-active finish. Refreshed on every
      // renderSections() call so it updates as the buyer picks finishes.
      if (section.footer) {
        const footerText = section.footer(this.sectionCurrentIds[idx] ?? null);
        if (footerText) {
          const footerEl = document.createElement('div');
          footerEl.className = 'material-panel__section-footer';
          footerEl.textContent = footerText;
          wrap.appendChild(footerEl);
        }
      }
      this.swatchList.appendChild(wrap);
    });
  }

  private makeSwatchButton(
    item: PanelItem,
    pressedId: string | null,
    onClick: (item: PanelItem) => void,
  ): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'material-swatch';
    btn.dataset.swatchId = item.id;
    btn.setAttribute('aria-pressed', String(item.id === pressedId));
    const dot = document.createElement('span');
    dot.className = 'material-swatch__dot';
    dot.style.background = item.dotColor ?? '#888';
    const label = document.createElement('span');
    label.className = 'material-swatch__label';
    label.textContent = item.label;
    btn.append(dot, label);
    btn.addEventListener('click', () => onClick(item));
    return btn;
  }
}

function rgbToCss(rgb: [number, number, number]): string {
  const clamp = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 200)));
  return `rgb(${clamp(rgb[0])}, ${clamp(rgb[1])}, ${clamp(rgb[2])})`;
}

function mustGet(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`[materialPanel] missing #${id}`);
  return el;
}
